/**
 * Per-page SEO config registry. Used by <SeoHead /> across all public pages.
 *
 * Rules (from /resources/SEO doc):
 *   - title ≤60 chars · description 140-160 chars
 *   - one focus keyword per page (in title + h1 + first 100 words)
 *   - bridge legacy "home care package" with new "support at home" terms
 */

export const SEO = {
    home: {
        title: "Wayly · AI for Australian Support at Home",
        description:
            "Decode aged-care statements, check classifications and plan budgets, Wayly's AI assistant helps Australian families understand Support at Home in plain English.",
        path: "/",
    },
    features: {
        title: "Features · Wayly",
        description:
            "Statement Decoder, Budget Calculator, Classification Self-Check and 5 more tools to make Support at Home (and legacy Home Care Package) admin simpler.",
        path: "/features",
    },
    pricing: {
        title: "Pricing · Wayly",
        description:
            "See Wayly plans for Australian families navigating Support at Home, start free, upgrade when you need full statement decoding and household coordination.",
        path: "/pricing",
    },
    trust: {
        title: "Trust & Safety · Wayly",
        description:
            "How Wayly keeps your aged-care data private and accurate: Australian-hosted, human-reviewed content, and clear AI disclaimers on every result.",
        path: "/trust",
    },
    demo: {
        title: "See Wayly in Action · Demo",
        description:
            "Watch how Wayly decodes a Support at Home statement, flags anomalies and explains charges in plain English, without signing up.",
        path: "/demo",
    },
    contact: {
        title: "Contact Wayly Support",
        description:
            "Reach Wayly's Australian support team for help with Support at Home statements, billing questions or partnership enquiries, usually under 24 hours.",
        path: "/contact",
    },
    forAdvisors: {
        title: "Wayly for Aged Care Advisers",
        description:
            "Help your clients understand Support at Home statements, budgets and classifications faster. Free Adviser plan with audit trail and client export.",
        path: "/for-advisors",
    },
    forGPs: {
        title: "Wayly for GPs & Health Pros",
        description:
            "Refer patients to Wayly to demystify Support at Home billing, classifications and reassessments, built for Australian families, free for advocacy use.",
        path: "/for-gps",
    },
    resources: {
        title: "Aged Care Resources · Wayly",
        description:
            "Plain-English guides, glossary and templates for Support at Home and Home Care Package families, written and reviewed by Australian aged-care experts.",
        path: "/resources",
    },
    articlesIndex: {
        title: "Articles · Wayly Aged Care Resources",
        description:
            "Wayly's plain-English articles on Support at Home statements, classifications, budgets and the 2026 program changes. Reviewed by aged-care experts.",
        path: "/resources/articles",
    },
    glossary: {
        title: "Aged Care Glossary · Wayly",
        description:
            "Plain-English Australian aged-care glossary: ACAT, Support at Home, AN-ACC, Home Care Package, classifications, contributions, written for families.",
        path: "/resources/glossary",
    },
    templates: {
        title: "Aged Care Templates · Wayly",
        description:
            "Free downloadable templates for Australian aged care: reassessment letters, complaint letters, statement query letters, drafted by experts, edit-ready.",
        path: "/resources/templates",
    },
    aiTools: {
        title: "Wayly Tools for Aged Care",
        description:
            "Nine free AI tools for Australian Support at Home: statement decoder, invoice checker, budget calculator, classification check, price checker and more, built for families.",
        path: "/ai-tools",
    },

    // ----- AI TOOLS (each is a SoftwareApplication) -----
    toolStatementDecoder: {
        title: "Statement Decoder: Check A Support At Home Statement",
        description:
            "Upload your Support at Home statement and Wayly's AI explains every charge in plain English, flags anomalies, double-billing and provider errors.",
        path: "/ai-tools/statement-decoder",
        toolName: "Statement Decoder",
        toolDesc:
            "Plain-English breakdown of any Australian Support at Home (or Home Care Package) statement. Spot anomalies, double-charges and unfair fees.",
        howTo: {
            name: "How to decode a Support at Home statement",
            description: "Three-step process to understand any aged-care statement.",
            steps: [
                { name: "Upload statement", text: "Upload your Support at Home or HCP statement (PDF or photo)." },
                { name: "Review plain-English summary", text: "Wayly shows what each line means and flags possible anomalies." },
                { name: "Take action", text: "Use Wayly's suggested questions to query unclear charges with your provider." },
            ],
        },
        faqs: [
            { q: "Does Wayly work with old Home Care Package statements?", a: "Yes. Wayly understands both legacy Home Care Package statements and the new Support at Home statements that replaced them on 1 November 2025." },
            { q: "Is my data shared with my provider?", a: "No. Your statements are private to you and anyone you invite to your Wayly household. Wayly does not share data with providers." },
            { q: "What anomalies does Wayly flag?", a: "Wayly highlights duplicate charges, fees outside your classification budget, missing rollover credits, services billed during a hospital stay, brokered service premiums, and care management above the 10% quarterly cap." },
        ],
    },
    toolBudgetCalculator: {
        title: "Budget Calculator: Support At Home Quarterly Budget",
        description:
            "Estimate your Support at Home quarterly budget by classification, see rollover allowances and plan care spending, works for legacy HCP budgets too.",
        path: "/ai-tools/budget-calculator",
        toolName: "Budget Calculator",
        toolDesc:
            "Estimate your Support at Home quarterly budget by classification level, see what rolls over, and plan care spending across the year.",
        howTo: {
            name: "Plan your Support at Home budget",
            description: "Quick walkthrough of Wayly's budget planner.",
            steps: [
                { name: "Pick classification", text: "Select your Support at Home classification (1, 8) or Home Care Package level." },
                { name: "Add expected services", text: "Enter your weekly care needs, Wayly maps these to allowable budget categories." },
                { name: "See rollover", text: "Wayly shows the up-to-$1,000-or-10% quarterly rollover you can keep, and care-management funds that roll annually." },
            ],
        },
        faqs: [
            { q: "How much budget rolls over each quarter?", a: "Up to $1,000 or 10% of your quarterly budget (whichever is higher) rolls into the next quarter. Care-management funds have no quarterly cap but are limited annually." },
            { q: "Does this work for Home Care Package budgets?", a: "Yes, Wayly bridges legacy HCP level 1, 4 budgets and Support at Home classifications 1, 8 so you can plan across the transition." },
        ],
    },
    toolPriceChecker: {
        title: "Aged Care Provider Price Checker · Wayly",
        description:
            "Compare your aged-care provider's rates against published prices and the Wayly Provider Quality Index. Spot brokered service premiums before they hit your budget.",
        path: "/ai-tools/provider-price-checker",
        toolName: "Provider Price Checker",
        toolDesc:
            "Check your provider's hourly rates against published prices and the Wayly Provider Quality Index, and surface any brokered service premiums.",
        faqs: [
            { q: "How do I tell if my provider is charging a fair price?", a: "Wayly compares the rate against published provider price lists for your service and area, weighs it against the Wayly Provider Quality Index, and flags brokered service premiums above the published rate." },
        ],
    },
    toolClassification: {
        title: "Classification Self-Check: Which Level Fits? | Wayly",
        description:
            "Quick self-check tool, answer 10 plain-English questions and Wayly estimates which Support at Home classification (1, 8) you'd likely be assessed at.",
        path: "/ai-tools/classification-self-check",
        toolName: "Classification Self-Check",
        toolDesc:
            "Estimate your likely Support at Home classification (1, 8) with a 5-minute self-assessment.",
    },
    toolReassessment: {
        title: "Letters & Follow-ups · Wayly",
        description:
            "Draft a polished letter to My Aged Care, your provider, ACQSC, or the Ombudsman. Wayly matches the tone to the recipient, tracks the reply, and knows when to escalate.",
        path: "/ai-tools/letters-and-follow-ups",
        toolName: "Letters & Follow-ups",
        toolDesc:
            "Draft letters to My Aged Care, providers, ACQSC, or the Ombudsman. Track responses and escalate on time.",
    },
    toolContribution: {
        title: "Contribution Estimator: Support At Home Fees | Wayly",
        description:
            "Estimate your Support at Home means-tested contributions before your assessment, Wayly walks you through the income and assets test in plain English.",
        path: "/ai-tools/contribution-estimator",
        toolName: "Contribution Estimator",
        toolDesc:
            "Estimate your means-tested contributions for Support at Home before assessment.",
    },
    toolCarePlan: {
        title: "Aged Support Plan Reviewer · Wayly",
        description:
            "Wayly's AI reviews your aged-care plan, highlights gaps, suggests questions and explains every clinical term, designed for non-clinical family carers.",
        path: "/ai-tools/care-plan-reviewer",
        toolName: "Support Plan Reviewer",
        toolDesc: "Plain-English review of any Support at Home or HCP care plan.",
    },
    toolFamilyCoordinator: {
        title: "Aged Care Q&A · Wayly",
        description:
            "Plain-English answers about the Support at Home program, grounded in the Aged Care Act 2024. A free Wayly tool, no account required.",
        path: "/ai-tools/family-coordinator",
        toolName: "Aged Care Q&A",
        toolDesc: "Plain-English answers about the Support at Home program, grounded in the Aged Care Act 2024.",
    },
    toolInvoiceChecker: {
        title: "Invoice Checker: Check An Aged Care Invoice | Wayly",
        description:
            "Upload the separate contribution invoice your Support at Home provider sends. Wayly checks it against your funding, expected contribution and the program rules, and flags anything worth raising before you pay.",
        path: "/ai-tools/invoice-checker",
        toolName: "Invoice Checker",
        toolDesc:
            "Check the separate contribution invoice your provider sends. Wayly compares it line by line against your funding, expected contribution and program rules.",
        howTo: {
            name: "How to check an aged-care invoice",
            description: "Three-step process to verify any Support at Home contribution invoice.",
            steps: [
                { name: "Upload invoice", text: "Upload the invoice your provider sent as a PDF, photo or screenshot." },
                { name: "Wayly checks the rules", text: "Wayly reads the invoice line by line, compares each charge against your funding and the program rules, and identifies anything worth raising." },
                { name: "Ask your provider", text: "Wayly drafts the exact question to ask your provider, and points you to the ACQSC on 1800 951 822 if it is not resolved." },
            ],
        },
        faqs: [
            { q: "Is the invoice different from the monthly statement?", a: "Yes. Your provider's monthly statement is information only. The invoice is what you actually pay, and it lives on a separate document sent by the provider. Wayly checks the invoice against the statement, your funding and the current program rules." },
            { q: "What kinds of issues does Wayly flag on an invoice?", a: "Wayly checks eleven things including duplicate charges, exit fees (not permitted under Support at Home), personal-care contributions dated after 1 October 2026 (fully funded from that date), care-management above the 10% cap, GST on care lines, and any unit price above your provider's own published price." },
            { q: "Will Wayly say my provider did something wrong?", a: "No. Wayly is careful to describe what it saw, why it might matter, and the specific question to ask. It never accuses your provider of overcharging or an error. Many flags turn out to have a legitimate explanation." },
            { q: "What happens on 1 October 2026?", a: "From 1 October 2026, personal care under Support at Home becomes fully government funded. Wayly will flag any personal-care contribution on invoices dated on or after that date so you can query it before paying." },
        ],
    },

    // ----- AUTH -----
    login: {
        title: "Sign in · Wayly",
        description: "Sign in to your Wayly account to view Support at Home statements, decoded charges and household updates.",
        path: "/login",
    },
    signup: {
        title: "Create your Wayly account",
        description: "Start your free Wayly account, decode Support at Home statements, plan care budgets and coordinate family carers in one place.",
        path: "/signup",
    },
};
